import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import contractService, { Contract, contractKindLabel, isOpenEndedContract, DEPOSIT_TEMPLATE_LABEL, DepositTemplate } from '../../services/contractService';
import AddendumFormModal from '../../components/contract/AddendumFormModal';
import { Link } from 'react-router-dom';
import rentalService, { Rental, RentalStatus } from '../../services/rentalService';
import { parseRouteId } from '../../utils/routeId';
import { showAlert, showConfirm } from '../../utils/alert';
import { getApiErrorMessage, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { RiArrowLeftLine, RiFileTextLine, RiListCheck, RiDownloadLine, RiEyeLine, RiEditLine, RiFileDownloadLine, RiDeleteBinLine, RiFilePdfLine, RiCalendarCloseLine, RiExchangeLine } from 'react-icons/ri';
import PDFLoadingModal from '../../components/ui/PDFLoadingModal';
import Layout from '../../components/ui/Layout';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ContractHistorySection from '../../components/rental/ContractHistorySection';
import DualScrollWrapper from '../../components/ui/DualScrollWrapper';
import { usePermission } from '../../hooks/usePermission';
import { terminateContractConfirmMessage } from '../../utils/contractConfirm';
import { statusColors } from '../../config/designSystem';


/** FB-2026-0006: การ์ดรายละเอียดบันทึกส่ง-มอบ (สัญญาแนบท้าย) */
const AddendumDetailCard: React.FC<{ contract: Contract; formatDate: (d: string | null | undefined) => string }> = ({ contract, formatDate }) => {
  const item = contract.contract_items?.[0];
  const money = (n?: number | null) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6" data-testid="addendum-detail">
      <h2 className="text-xl font-semibold text-secondary-800 mb-4 flex items-center">
        <RiListCheck className="w-5 h-5 mr-2" />
        บันทึกการส่ง–มอบ (แนบท้ายสัญญาเช่าหลัก {contract.master_contract_number || ''})
      </h2>
      {item ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div><span className="text-secondary-600">เลขที่การเช่า:</span> <span className="text-secondary-900">{item.rental_number || (item.rental_id ? `#${item.rental_id}` : '-')}</span></div>
          <div><span className="text-secondary-600">ยี่ห้อ / รุ่น:</span> <span className="text-secondary-900">{[item.device_brand, item.device_model].filter(Boolean).join(' ') || '-'}</span></div>
          <div><span className="text-secondary-600">หมายเลขเครื่อง:</span> <span className="text-secondary-900">{item.device_serial || '-'}</span></div>
          <div><span className="text-secondary-600">โปรแกรม:</span> <span className="text-secondary-900">{item.program_name || '-'}</span></div>
          <div><span className="text-secondary-600">ลูกค้า (โครงการ):</span> <span className="text-secondary-900">{item.customer_name || '-'}</span></div>
          <div><span className="text-secondary-600">เช่าขั้นต่ำ:</span> <span className="text-secondary-900">{item.rental_period_months || '-'} เดือน นับจาก {formatDate(item.item_start_date)}</span></div>
          <div><span className="text-secondary-600">ค่าเช่า:</span> <span className="text-secondary-900">{money(item.rental_rate)} บาท/เดือน (ยังไม่รวม VAT)</span></div>
          <div><span className="text-secondary-600">เริ่มชำระงวดแรก:</span> <span className="text-secondary-900">{formatDate(item.first_payment_date)}</span></div>
          <div><span className="text-secondary-600">เงินประกัน:</span> <span className="text-secondary-900">{money(item.deposit_amount)} บาท</span></div>
          <div className="md:col-span-3"><span className="text-secondary-600">แบบเอกสาร:</span>{' '}
            <span className="text-secondary-900">{contract.deposit_template ? DEPOSIT_TEMPLATE_LABEL[contract.deposit_template as DepositTemplate] : '-'}</span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-secondary-500">ไม่พบรายละเอียดอุปกรณ์ของบันทึกนี้</p>
      )}
    </div>
  );
};

const ContractDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canView, canEdit, canDelete, hasPermission } = usePermission();
  const [contract, setContract] = useState<Contract | null>(null);
  const [contractRentals, setContractRentals] = useState<Rental[]>([]);
  const [contractDocuments, setContractDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRentals, setLoadingRentals] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [downloadingSubContract, setDownloadingSubContract] = useState<number | null>(null);
  // FB-2026-0006: แนบท้ายของสัญญาหลัก + โมดัลเพิ่ม/แก้ไขแนบท้าย
  const [addenda, setAddenda] = useState<Contract[]>([]);
  const [loadingAddenda, setLoadingAddenda] = useState(false);
  const [addendumModal, setAddendumModal] = useState<{ main?: Contract | null; addendum?: Contract | null } | null>(null);

  // ตรวจสอบ permission
  useEffect(() => {
    if (!canView('contracts')) {
      showAlert('ไม่มีสิทธิ์เข้าถึง', 'คุณไม่มีสิทธิ์ในการดูรายละเอียดสัญญา', 'error');
      navigate('/contracts');
    }
  }, [canView, navigate]);

  // BUG-S2283-001: id ต้องเป็นจำนวนเต็มบวกล้วน — ไม่ถูกต้อง = ไม่ยิง API แสดง "ไม่พบข้อมูลสัญญา"
  const contractId = parseRouteId(id);

  useEffect(() => {
    if (contractId === null) {
      setContract(null);
      setLoading(false);
      return;
    }
    loadContractDetail(contractId);
  }, [contractId]);
  
  // Debug effect
  useEffect(() => {
  }, [downloadingPDF, pdfProgress]);

  // silent = โหลดใหม่โดยไม่ขึ้นหน้าโหลด — หน้าโหลด (early return) ถอดโมดัลแนบท้ายทิ้งกลางคัน
  // แล้วผู้ใช้ไม่เห็นปุ่ม "ดาวน์โหลด PDF บันทึกส่ง-มอบ" หลังบันทึก (E2E 3 พ.ย.)
  const loadContractDetail = async (contractId: number, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const contractData = await contractService.getContract(contractId);
      setContract(contractData);

      // FB-2026-0006: สัญญาหลัก → โหลดสัญญาแนบท้ายทั้งหมด
      if (contractData?.contract_type === 'MAIN') {
        loadAddenda(contractId);
      } else {
        setAddenda([]);
      }

      
      // โหลดรายการเช่าที่ผูกกับสัญญานี้
      await loadContractRentals(contractId);
      
      // โหลดเอกสารแนบ
      await loadContractDocuments(contractId);
    } catch (error) {
      console.error('Error loading contract detail:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลสัญญาได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAddenda = async (mainId: number) => {
    try {
      setLoadingAddenda(true);
      const subs = await contractService.getSubContracts(mainId);
      setAddenda(Array.isArray(subs) ? subs : []);
    } catch (error) {
      console.error('Error loading addenda:', error);
      setAddenda([]);
    } finally {
      setLoadingAddenda(false);
    }
  };

  const downloadOnePdf = async (target: Contract, includeAddenda = false) => {
    try {
      await contractService.downloadAndSaveContractPDF(target, includeAddenda ? { include_addenda: true } : undefined);
    } catch (error) {
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถดาวน์โหลดไฟล์ PDF ได้'), 'error');
    }
  };

  const loadContractRentals = async (contractId: number) => {
    try {
      setLoadingRentals(true);
      // โหลดรายการเช่าที่ผูกกับสัญญานี้ (เฉพาะสถานะ ACTIVE)
      const response = await rentalService.getRentals({ 
        contract_id: contractId,
        status: RentalStatus.ACTIVE, // แสดงเฉพาะรายการเช่าที่ยังใช้งานอยู่
        limit: 100 
      });
      setContractRentals(response.items);
    } catch (error) {
      console.error('Error loading contract rentals:', error);
    } finally {
      setLoadingRentals(false);
    }
  };

  const loadContractDocuments = async (contractId: number) => {
    try {
      setLoadingDocuments(true);
      const documents = await contractService.getContractDocuments(contractId);
      setContractDocuments(documents);
    } catch (error) {
      console.error('Error loading contract documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDownloadDocument = async (documentId: number, fileName: string) => {
    try {
      const response = await contractService.downloadContractDocument(documentId);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showAlert('สำเร็จ', 'ดาวน์โหลดเอกสารเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error downloading document:', error);
      // BUG-S2077-004: response แบบ blob → ข้อความ backend (เช่น 403 "คุณไม่มีสิทธิ์เข้าถึงเอกสารนี้…") อ่านแบบ sync ไม่เห็น
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถดาวน์โหลดเอกสารได้'), 'error');
    }
  };

  const handleDownloadPDF = async () => {
    if (!contract) return;
    
    setDownloadingPDF(true);
    setPdfProgress(0);
    
    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setPdfProgress(prev => {
        const newProgress = prev >= 90 ? 90 : prev + 10;
        if (prev >= 90) {
          clearInterval(progressInterval);
        }
        return newProgress;
      });
    }, 200);
    
    try {
      // เพิ่ม delay เล็กน้อยเพื่อให้ Modal แสดง
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const blob = await contractService.downloadContractPDF(contract.id);
      
      // Complete the progress
      clearInterval(progressInterval);
      setPdfProgress(100);
      
      // Show 100% completion for a moment
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract-${contract.contract_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Hide modal before showing success alert
      setDownloadingPDF(false);
      setPdfProgress(0);
      
      // Small delay before alert
      await new Promise(resolve => setTimeout(resolve, 100));
      showAlert('สำเร็จ', 'ดาวน์โหลดไฟล์ PDF เรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      clearInterval(progressInterval);
      setDownloadingPDF(false);
      setPdfProgress(0);
      // BUG-S2077-004: response แบบ blob → ข้อความ backend (เช่น 403 "คุณไม่มีสิทธิ์เข้าถึงเอกสารนี้…") อ่านแบบ sync ไม่เห็น
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถดาวน์โหลดไฟล์ PDF ได้'), 'error');
    }
  };

  // FB-2026-0004: end_date ของสัญญาหลัก/แนบท้าย = null → เดิม new Date(null) ขึ้นวันที่ปี 1970
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'long',
      day: 'numeric',
    });
  };

  // ฟังก์ชันสำหรับการแก้ไขสัญญา
  const handleEditContract = () => {
    if (!contract) return;
    if (contract.contract_type === 'ADDENDUM') {
      setAddendumModal({ addendum: contract });
      return;
    }
    // ทุกสถานะไปหน้าแก้ไขเดิม (หน้าแก้ไขจะจัดการเหตุผลการแก้ไขเอง)
    navigate(`/contracts/edit/${contract.id}`);
  };

  // ฟังก์ชันสำหรับยกเลิกสัญญา (BUG-139)
  const handleTerminateContract = () => {
    if (!contract) return;
    showConfirm(
      'ยืนยันการยกเลิกสัญญา',
      terminateContractConfirmMessage(contract.contract_number, contract.contract_type),
      async () => {
        try {
          await contractService.terminateContract(contract.id);
          showAlert('ยกเลิกสัญญาสำเร็จ', 'สัญญาถูกยกเลิกเรียบร้อยแล้ว', 'success');
          loadContractDetail(contract.id);
        } catch (error: any) {
          console.error('Error terminating contract:', error);
          // BUG-S2089-001: สถานะอาจถูกเปลี่ยนจากที่อื่น → โหลดใหม่ให้ปุ่ม/สถานะตรงความจริง
          loadContractDetail(contract.id);
          const message = getApiErrorMessage(error, 'ไม่สามารถยกเลิกสัญญาได้');
          showAlert('เกิดข้อผิดพลาด', message, 'error');
        }
      },
      { confirmText: 'ยกเลิกสัญญา', cancelText: 'ย้อนกลับ', isDanger: true }
    );
  };

  // ฟังก์ชันสำหรับต่อสัญญา (BUG-140)
  const handleRenewContract = () => {
    if (!contract) return;
    showConfirm(
      'ยืนยันการต่อสัญญา',
      `คุณต้องการต่อสัญญา ${contract.contract_number} ใช่หรือไม่? ระบบจะสร้างสัญญาใหม่โดยอ้างอิงจากสัญญานี้`,
      async () => {
        try {
          const result = await contractService.renewContract(contract.id);
          showAlert(
            'ต่อสัญญาสำเร็จ',
            `สร้างสัญญาใหม่ ${result.new_contract_number} เรียบร้อยแล้ว`,
            'success'
          );
          // นำทางไปยังสัญญาใหม่
          navigate(`/contracts/view/${result.new_contract_id}`);
        } catch (error: any) {
          console.error('Error renewing contract:', error);
          const message = getApiErrorMessage(error, 'ไม่สามารถต่อสัญญาได้');
          showAlert('เกิดข้อผิดพลาด', message, 'error');
        }
      },
      { confirmText: 'ต่อสัญญา', cancelText: 'ย้อนกลับ' }
    );
  };

  // ฟังก์ชันสำหรับดาวน์โหลดสัญญาลูก (sub-contract) สำหรับแต่ละรายการเช่า
  const handleDownloadSubContractPDF = async (rentalId: number, rentalNumber: string) => {
    try {
      setDownloadingSubContract(rentalId);
      
      
      // ใช้ endpoint สำหรับดาวน์โหลดสัญญาลูก (sub-contract) 
      // ส่ง rental_id เพื่อสร้าง PDF เฉพาะรายการเช่านั้น
      const response = await contractService.downloadContractPDF(contractId!, { rental_id: rentalId });
      
      // สร้าง Blob จากข้อมูลที่ได้
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // สร้าง link สำหรับดาวน์โหลด
      const link = document.createElement('a');
      link.href = url;
      link.download = `sub_contract_${contract?.contract_number}_${rentalNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // ลบ link และ URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showAlert('สำเร็จ', 'ดาวน์โหลดสัญญาลูกเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error downloading sub-contract PDF:', error);
      // BUG-S2077-004: response แบบ blob → ข้อความ backend (เช่น 403 "คุณไม่มีสิทธิ์เข้าถึงเอกสารนี้…") อ่านแบบ sync ไม่เห็น
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถดาวน์โหลดสัญญาลูกได้'), 'error');
    } finally {
      setDownloadingSubContract(null);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-success-100 text-success-800';
      case 'DRAFT':
        return 'bg-secondary-100 text-secondary-800';
      case 'EXPIRED':
        return 'bg-danger-100 text-danger-800';
      case 'TERMINATED':
        return 'bg-danger-100 text-danger-800';
      default:
        return 'bg-secondary-100 text-secondary-800';
    }
  };

  // BUG-S2098-001: ป้ายสถานะมาจาก statusColors.contract (แหล่งเดียวกับหน้ารายการ/รายงาน) — เดิม ACTIVE
  // ขึ้น "ใช้งานอยู่" ทั้งที่หน้ารายการ กล่องยืนยัน และข้อความ backend ใช้ "มีผลบังคับใช้"
  const getStatusText = (status: string): string =>
    (statusColors.contract as Record<string, { label: string }>)[status]?.label ?? status;

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-info-600 mx-auto"></div>
          <p className="mt-2 text-secondary-600">กำลังโหลดข้อมูลสัญญา...</p>
        </div>
      </Layout>
    );
  }

  if (!contract) {
    return (
      <Layout>
        <div className="text-center py-8">
          <p className="text-secondary-600">ไม่พบข้อมูลสัญญา</p>
          <button
            onClick={() => navigate('/contracts')}
            className="mt-4 px-4 py-2 bg-info-600 text-white rounded-lg hover:bg-info-700"
          >
            กลับไปหน้ารายการสัญญา
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'สัญญา', path: '/contracts/list' },
        { label: 'รายละเอียด' }
      ]} />

      {/* Header - Executive Navy Theme */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiFileTextLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">รายละเอียดสัญญา</h1>
            <p className="text-xs text-secondary-500 mt-0.5">ดูข้อมูลรายละเอียดสัญญาเช่าอุปกรณ์</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* ปุ่มดาวน์โหลด PDF สัญญาทั้งฉบับ (เหมือนเมนูในหน้าจัดการสัญญา) */}
          <button
            onClick={handleDownloadPDF}
            className="inline-flex items-center gap-1.5 px-3 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
            title="ดาวน์โหลดเอกสารสัญญาทั้งฉบับเป็น PDF"
          >
            <RiFileDownloadLine className="w-4 h-4" />
            {contract.contract_type === 'ADDENDUM' ? 'ดาวน์โหลด PDF บันทึกส่ง-มอบ' : 'ดาวน์โหลด PDF'}
          </button>
          {contract.contract_type === 'MAIN' && addenda.length > 0 && (
            <button
              onClick={() => downloadOnePdf(contract, true)}
              className="inline-flex items-center gap-1.5 px-3 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
              title="สัญญาหลัก + สัญญาแนบท้ายที่ยังไม่ยกเลิกทั้งหมดในไฟล์เดียว"
            >
              <RiFilePdfLine className="w-4 h-4" />
              พิมพ์สัญญาหลัก+แนบท้ายทั้งหมด
            </button>
          )}
          {/* ปุ่มต่อสัญญา - แสดงเฉพาะสถานะ ACTIVE
              BUG-S1529-001 FIX: backend renew_contract (contracts.py) อนุญาตเฉพาะ ACTIVE
              ("ต่ออายุได้เฉพาะสัญญาที่กำลังใช้งานเท่านั้น"). เดิม FE แสดงปุ่มนี้กับ EXPIRED ด้วย
              → ปุ่มที่กดแล้ว 400 เสมอ (FE/BE mismatch). ปรับ FE ให้ตรงกับ policy ที่ backend
              บังคับใช้จริง. หมายเหตุ: คำถามเชิงนโยบาย "ควรต่ออายุสัญญาที่หมดอายุได้หรือไม่"
              เป็นประเด็นที่ต้องตัดสินใจกับ PO (ถ้าใช่ → ต้องแก้ backend guard) */}
          {/* BUG-QA-027 FIX (S88): backend /renew = contracts.create (ไม่ใช่ edit) */}
          {contract.contract_type === 'MAIN' && hasPermission('contracts.create')
            && (contract.status === 'ACTIVE' || contract.status === 'DRAFT') && (
            <button
              onClick={() => setAddendumModal({ main: contract })}
              className="inline-flex items-center gap-1.5 px-3 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              <RiListCheck className="w-4 h-4" />
              เพิ่มสัญญาแนบท้าย
            </button>
          )}
          {contract.contract_type === 'ADDENDUM' && contract.status === 'DRAFT' && hasPermission('contracts.edit') && (
            <button
              onClick={handleEditContract}
              className="inline-flex items-center gap-1.5 px-3 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-primary-700 bg-white border border-primary-200 rounded-md hover:bg-primary-50 transition-colors"
            >
              <RiEditLine className="w-4 h-4" />
              แก้ไขสัญญาแนบท้าย
            </button>
          )}
          {/* FB-2026-0004: สัญญาแบบเปิด (สัญญาหลัก/แนบท้าย) ไม่มีวันสิ้นสุด → ไม่มีการต่ออายุ */}
          {contract.status === 'ACTIVE' && hasPermission('contracts.create') && !isOpenEndedContract(contract) && (
            <button
              onClick={handleRenewContract}
              className="inline-flex items-center gap-1.5 px-3 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              <RiExchangeLine className="w-4 h-4" />
              ต่อสัญญา
            </button>
          )}
          {/* ปุ่มยกเลิกสัญญา - แสดงเมื่อสถานะ ACTIVE */}
          {/* BUG-QA-027 FIX (S88): backend /terminate = contracts.terminate (ไม่ใช่ edit) */}
          {contract.status === 'ACTIVE' && hasPermission('contracts.terminate') && (
            <button
              onClick={handleTerminateContract}
              className="inline-flex items-center gap-1.5 px-3 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-white bg-danger-600 rounded-md hover:bg-danger-700 transition-colors"
            >
              <RiCalendarCloseLine className="w-4 h-4" />
              ยกเลิกสัญญา
            </button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* ข้อมูลสัญญา */}
        <div>
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-secondary-800 flex items-center">
                <RiFileTextLine className="w-5 h-5 mr-2" />
                ข้อมูลสัญญา
              </h2>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                {getStatusText(contract.status)}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">เลขที่สัญญา</label>
                <p className="text-secondary-900 font-medium">{contract.contract_number}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">บริษัท</label>
                <p className="text-secondary-900">{contract.company?.name || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">ประเภทสัญญา</label>
                <p className="text-secondary-900" data-testid="contract-kind">
                  {contractKindLabel(contract)}
                  {contract.contract_type === 'ADDENDUM' && contract.master_contract_id && (
                    <>
                      {' · '}
                      <Link to={`/contracts/view/${contract.master_contract_id}`} className="text-primary-700 underline">
                        ไปที่สัญญาหลัก
                      </Link>
                    </>
                  )}
                </p>
              </div>

              {contract.contract_date && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    {contract.contract_type === 'ADDENDUM' ? 'วันที่บันทึก' : 'วันที่ทำสัญญา'}
                  </label>
                  <p className="text-secondary-900">{formatDate(contract.contract_date)}</p>
                </div>
              )}

              {isOpenEndedContract(contract) ? (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">ระยะเวลา</label>
                  <p className="text-secondary-900" data-testid="open-ended">
                    สัญญาแบบเปิด (ไม่มีวันสิ้นสุด)
                    {contract.contract_type === 'ADDENDUM' && <> · เริ่มเช่า {formatDate(contract.start_date)}</>}
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">วันที่เริ่มต้น</label>
                    <p className="text-secondary-900">{formatDate(contract.start_date)}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">วันที่สิ้นสุด</label>
                    <p className="text-secondary-900">{formatDate(contract.end_date)}</p>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">เงื่อนไขการชำระ</label>
                <p className="text-secondary-900">
                  {contract.payment_term === 'MONTHLY' && 'รายเดือน'}
                  {contract.payment_term === 'QUARTERLY' && 'รายไตรมาส'}
                  {contract.payment_term === 'YEARLY' && 'รายปี'}
                </p>
              </div>
            </div>

            {/* BUG-S2082-002: ข้อกำหนดที่กรอกตอนสร้าง/แก้ไขไม่เคยแสดงที่ไหนเลยนอกจากฟอร์มแก้ไข */}
            {contract.terms_conditions && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-secondary-700 mb-1">ข้อกำหนดและเงื่อนไขเพิ่มเติม</label>
                <p className="text-secondary-900 bg-secondary-50 p-3 rounded-lg whitespace-pre-wrap break-words">{contract.terms_conditions}</p>
              </div>
            )}

            {contract.notes && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-secondary-700 mb-1">หมายเหตุ</label>
                <p className="text-secondary-900 bg-secondary-50 p-3 rounded-lg">{contract.notes}</p>
              </div>
            )}
          </div>

          {/* FB-2026-0006: รายละเอียดบันทึกส่ง-มอบ (สัญญาแนบท้าย) */}
          {contract.contract_type === 'ADDENDUM' && (
            <AddendumDetailCard contract={contract} formatDate={formatDate} />
          )}

          {/* FB-2026-0006: รายการสัญญาแนบท้ายของสัญญาหลัก */}
          {contract.contract_type === 'MAIN' && (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6" data-testid="addenda-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-secondary-800 flex items-center">
                  <RiListCheck className="w-5 h-5 mr-2" />
                  สัญญาแนบท้าย (บันทึกส่ง-มอบ) {addenda.length > 0 ? `${addenda.length} ฉบับ` : ''}
                </h2>
                {hasPermission('contracts.create') && (contract.status === 'ACTIVE' || contract.status === 'DRAFT') && (
                  <button
                    onClick={() => setAddendumModal({ main: contract })}
                    className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100"
                  >
                    + เพิ่มสัญญาแนบท้าย
                  </button>
                )}
              </div>
              {loadingAddenda ? (
                <p className="text-sm text-secondary-500">กำลังโหลดสัญญาแนบท้าย...</p>
              ) : addenda.length === 0 ? (
                <p className="text-sm text-secondary-500">ยังไม่มีสัญญาแนบท้าย — กด "เพิ่มสัญญาแนบท้าย" เพื่อเลือกรายการเช่าของบริษัทนี้</p>
              ) : (
                <DualScrollWrapper className="border border-secondary-200 rounded-lg">
                  <table className="min-w-full divide-y divide-secondary-200" aria-label="สัญญาแนบท้าย">
                    <thead className="bg-secondary-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">เลขที่บันทึก</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">เลขที่การเช่า</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">S/N</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">โครงการ</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-secondary-600 whitespace-nowrap">ค่าเช่า/เดือน</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">สถานะ</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap">PDF</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {addenda.map((a) => {
                        const it = a.contract_items?.[0];
                        return (
                          <tr key={a.id}>
                            <td className="px-3 py-2 text-sm whitespace-nowrap">
                              <Link to={`/contracts/view/${a.id}`} className="text-primary-700 font-medium hover:underline">{a.contract_number}</Link>
                            </td>
                            <td className="px-3 py-2 text-sm whitespace-nowrap">{it?.rental_number || (it?.rental_id ? `#${it.rental_id}` : '-')}</td>
                            <td className="px-3 py-2 text-sm whitespace-nowrap">{it?.device_serial || '-'}</td>
                            <td className="px-3 py-2 text-sm">{it?.customer_name || a.site?.name || '-'}</td>
                            <td className="px-3 py-2 text-sm whitespace-nowrap text-right">
                              {it ? Number(it.rental_rate || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }) : '-'}
                            </td>
                            <td className="px-3 py-2 text-sm whitespace-nowrap">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(a.status)}`}>{getStatusText(a.status)}</span>
                            </td>
                            <td className="px-3 py-2 text-sm whitespace-nowrap">
                              <button onClick={() => downloadOnePdf(a)} className="text-info-600 hover:text-info-800" title="ดาวน์โหลด PDF บันทึกส่ง-มอบ" aria-label={`ดาวน์โหลด PDF ${a.contract_number}`}>
                                <RiFileDownloadLine className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </DualScrollWrapper>
              )}
            </div>
          )}

          {/* ประวัติสัญญา */}
          <ContractHistorySection contractId={contract.id} />

          {/* รายการเช่าในสัญญา */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-secondary-800 mb-4 flex items-center">
              <RiListCheck className="w-5 h-5 mr-2" />
              รายการเช่าที่อยู่ในสัญญา
            </h2>

            {loadingRentals ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-info-600 mx-auto"></div>
                <p className="mt-2 text-sm text-secondary-600">กำลังโหลดรายการเช่า...</p>
              </div>
            ) : contractRentals.length > 0 ? (
              <DualScrollWrapper className="border border-secondary-200 rounded-lg">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50 border-b-2 border-secondary-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        เลขที่การเช่า
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        สินค้า
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        อุปกรณ์
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        วันที่เริ่ม
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        วันที่สิ้นสุด
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        ค่าเช่า/เดือน
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        ระยะเวลา
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        การกระทำ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {contractRentals.map((rental) => (
                      <tr key={rental.id}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                          {rental.code || rental.rental_number || '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                          {rental.product_name || 
                           rental.product?.name || 
                           (rental.rental_items && rental.rental_items[0]?.product?.name) ||
                           (rental.rental_items && rental.rental_items[0]?.product_name) ||
                           'ไม่ระบุสินค้า'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {rental.device_name || rental.device?.serial_number || '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {rental.rental_date ? formatDate(rental.rental_date) : '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {rental.expected_return_date ? formatDate(rental.expected_return_date) : '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {rental.monthly_fee?.toLocaleString() || '-'} บาท
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {rental.period_months || '-'} เดือน
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            {/* G-13: ลิงก์ข้าม module ต้องมีสิทธิ์ปลายทาง (rentals.view) */}
                            {hasPermission('rentals.view') && (
                              <button
                                onClick={() => navigate(`/rentals/view/${rental.id}`)}
                                className="text-info-600 hover:text-info-800"
                                title="ดูรายละเอียด"
                              >
                                <RiEyeLine className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDownloadSubContractPDF(rental.id, rental.code || rental.rental_number || `${rental.id}`)}
                              disabled={downloadingSubContract === rental.id}
                              className={`${
                                downloadingSubContract === rental.id
                                  ? 'text-secondary-400 cursor-not-allowed'
                                  : 'text-success-600 hover:text-success-800'
                              }`}
                              title="ดาวน์โหลดสัญญาลูก"
                            >
                              {downloadingSubContract === rental.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-success-600 border-t-transparent" />
                              ) : (
                                <RiFilePdfLine className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DualScrollWrapper>
            ) : (
              <div className="text-center py-4 text-secondary-500">
                ไม่มีรายการเช่าในสัญญานี้
              </div>
            )}
          </div>

          {/* เอกสารแนบ */}
          <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
            <h2 className="text-xl font-semibold text-secondary-800 mb-4 flex items-center">
              <RiFileTextLine className="w-5 h-5 mr-2" />
              เอกสารแนบ
            </h2>

            {loadingDocuments ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-info-600 mx-auto"></div>
                <p className="mt-2 text-sm text-secondary-600">กำลังโหลดเอกสารแนบ...</p>
              </div>
            ) : contractDocuments.length > 0 ? (
              <DualScrollWrapper className="border border-secondary-200 rounded-lg">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50 border-b-2 border-secondary-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        ชื่อเอกสาร
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        ประเภท
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        วันที่อัพโหลด
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        ผู้อัพโหลด
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600">
                        การกระทำ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {contractDocuments.map((doc) => (
                      <tr key={doc.id}>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-900">
                          {doc.title}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {doc.type === 'contract' ? 'สัญญา' : doc.type}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {new Date(doc.created_at).toLocaleDateString('th-TH')}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {doc.created_by?.username || '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleDownloadDocument(doc.id, doc.title)}
                            className="text-info-600 hover:text-info-800"
                            title="ดาวน์โหลด"
                          >
                            <RiFileDownloadLine className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DualScrollWrapper>
            ) : (
              <div className="text-center py-4 text-secondary-500">
                ไม่มีเอกสารแนบ
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PDF Loading Modal */}
      <PDFLoadingModal isOpen={downloadingPDF} progress={pdfProgress} />

      <AddendumFormModal
        isOpen={addendumModal !== null}
        onClose={() => setAddendumModal(null)}
        mainContract={addendumModal?.main ?? null}
        addendum={addendumModal?.addendum ?? null}
        onSaved={() => { if (contractId !== null) loadContractDetail(contractId, true); }}
      />

    </Layout>
  );
};

export default ContractDetailPage;